import { Spin } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { init as echartsInit } from 'echarts';
import { ChartData } from '@/views/pages/Dashboard';
import publicMethod from '@/utils/publicMethod';

const Chart = ({
    data,
    loading,
    title,
    text = 'No. of online',
    dateType,
}: {
    data: ChartData | null;
    loading: boolean;
    title: string;
    text?: string;
    dateType: string;
}) => {
    const chartRef = useRef<echarts.ECharts | null>(null);
    const chartBoxRef = useRef<HTMLDivElement | null>(null);
    const [tabIndex, setTabIndex] = useState(0);

    const initChart = () => {
        chartRef.current = echartsInit(chartBoxRef.current as HTMLDivElement);
        const { xdata, ydata } = data!;
        const option = {
            title: {
                text,
                textStyle: {
                    color: '#1A1E1F',
                    fontWeight: 'normal',
                    fontSize: '12px',
                    fontFamily: 'Regular',
                },
            },
            grid: {
                left: '1%',
                right: '1%',
                bottom: '1%',
                top: '13%',
                containLabel: true,
            },
            xAxis: {
                data: xdata,
                type: 'category',
                boundaryGap: true,
                axisTick: {
                    show: false,
                },
                axisLabel: {
                    textStyle: {
                        color: '#1A1E1F',
                        fontSize: '12px',
                        fontFamily: 'Regular',
                    },
                    interval: 'auto',
                    formatter: function (value) {
                        return dayjs(new Date(value)).format(
                            dateType === 'today' || dateType === 'yesterday'
                                ? 'hh A'
                                : dateType === 'financial' || dateType === 'prev'
                                ? 'MM/YYYY'
                                : 'DD MMM YYYY'
                        );
                    },
                },
            },
            yAxis: {
                type: 'value',
                axisLine: {
                    show: false,
                },
                axisTick: {
                    show: false,
                },
                axisLabel: {
                    textStyle: {
                        color: '#1A1E1F',
                        fontSize: '12px',
                        fontFamily: 'Regular',
                    },
                },
                splitLine: {
                    interval: 60,
                    show: true,
                    lineStyle: {
                        width: 1,
                        type: 'dashed',
                        color: '#3D3D3D',
                    },
                },
            },
            series:
                title === 'Total Market Value'
                    ? [
                          {
                              name: '',
                              data: ydata ?? [],
                              type: 'line',
                              smooth: false,
                              symbol: 'none',
                              lineStyle: {
                                  color: '#7C4DFF',
                                  width: 4,
                              },
                          },
                      ]
                    : [
                          {
                              name: 'customer',
                              data: ydata.find(item => item.name === 'customer')?.data ?? [],
                              type: 'line',
                              smooth: false,
                              symbol: 'none',
                              lineStyle: {
                                  color: '#7C4DFF',
                                  width: 4,
                              },
                          },
                          {
                              name: 'tasker',
                              data: ydata.find(item => item.name === 'tasker')?.data ?? [],
                              type: 'line',
                              smooth: false,
                              symbol: 'none',
                              lineStyle: {
                                  color: '#40C4FF',
                                  width: 4,
                              },
                          },
                      ],
            tooltip: {
                trigger: 'axis',
                textStyle: {
                    color: '#FFFFFF',
                    fontSize: '12',
                    fontFamily: 'Regular',
                },
                extraCssText: 'border-radius: 8px;width:180px;',
                backgroundColor: 'RGBA(95, 97, 98, 1)',
                // position: function (point, params, dom, rect, size) {
                //     return [point[0], '10%'];
                // },
                formatter: function (param) {
                    console.log(Number(param[0].value));
                    return [
                        `
                            <div style='padding:8px;border-radius:8px;'>
                                ${dayjs(new Date(param[0].name)).format('hh:mm A, DD MMM YYYY')}
                                <div style='display:flex;align-item:center;margin:8px 0 4px 0;'>
                                    ${
                                        title === 'Total Market Value'
                                            ? ''
                                            : `<span style='width:18px;height:18px;border-radius:4px;background:#7C4DFF;margin-right:4px;'></span><span>Customers</span>`
                                    }
                                    <span style='margin-left:${title === 'Total Market Value' ? 'unset' : 'auto'};'>${
                            title === 'Total Market Value'
                                ? '$' + publicMethod.formatPrice(Number(param[0].value).toFixed(2))
                                : param[0].value
                        }</span>
                                </div>
                                ${
                                    param[1]
                                        ? `<div style='display:flex;align-item:center;'>
                                    <span style='width:18px;height:18px;border-radius:4px;background:#40C4FF;margin-right:4px;'></span>
                                    <span>Taskers</span>
                                    <span style='margin-left:auto;'>${param[1].value}</span>
                                </div>`
                                        : ''
                                }
                            </div>
                        `,
                    ].join('');
                },
            },
        };
        chartRef.current.setOption(option);
    };

    useEffect(() => {
        if (!data?.xdata || !data?.ydata) return;
        // const { xdata, ydata } = data!;
        // const customerData = ydata.find(item => item.name === 'customer')?.data;
        // const taskerData = ydata.find(item => item.name === 'tasker')?.data;
        initChart();

        window.addEventListener('resize', function () {
            chartRef.current?.resize();
        });
        return () => {
            chartRef.current?.dispose();
            window.removeEventListener('resize', function () {
                chartRef.current?.resize();
            });
        };
    }, [data]);

    return (
        <div className="chart">
            <h3>{title}</h3>
            <Spin spinning={loading}>
                {data ? (
                    <div ref={chartBoxRef} id="lineChartBox" style={{ display: 'block' }}></div>
                ) : (
                    <div className="no-chartBox" style={{ display: 'flex' }}>
                        No Data
                    </div>
                )}
            </Spin>

            {title !== 'Total Market Value' && Boolean(data) && (
                <div className="flex-center" style={{ marginTop: 20 }}>
                    <div className="customer_box">Customers</div>
                    <div className="tasker_box">Taskers</div>
                </div>
            )}
        </div>
    );
};

export default Chart;
